import { useHistory, useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import {
	Button,
	Flex,
	FormControl,
	FormErrorMessage,
	FormLabel,
	Grid,
	HStack,
	Input,
	Skeleton,
	Switch,
	Text,
	useColorModeValue,
	useToast
} from '@chakra-ui/react';
import InputMask from 'react-input-mask';

import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import { useAssociate, useUpdateAssociate } from 'hooks/associates';
import { phoneRegex } from 'variables/regex';

const schema = yup.object().shape({
	name: yup.string().required('O campo Nome é obrigatório'),
	phone: yup
		.string()
		.required('O campo Telefone é obrigatório')
		.matches(phoneRegex, 'Insira um telefone válido')
});

const EditAssociate = () => {
	const { id } = useParams();
	const { data, loading } = useAssociate(id);
	const associate = data?.getAssociate || {};

	const history = useHistory();

	const methods = useForm({
		mode: 'onBlur',
		reValidateMode: 'onBlur',
		resolver: yupResolver(schema),
		defaultValues: associate
	});
	const toast = useToast();

	const {
		register,
		reset,
		handleSubmit,
		formState: { errors }
	} = methods;

	const [updateAssociate, { loading: loadingUpdate }] = useUpdateAssociate({
		onSuccess: (data) => {
			reset();
			toast({
				title: 'Associado atualizado com sucesso.',
				status: 'success',
				duration: 5000,
				isClosable: true
			});
			history.push(
				`/sistema/administracao/associados/${data.updateAssociate.id}`
			);
		},
		onError: () => {
			toast({
				title: 'Ocorreu um erro',
				status: 'error',
				duration: 5000,
				isClosable: true
			});
		}
	});

	const onSubmit = (data) => {
		data.phone = data.phone
			.replaceAll('(', '')
			.replaceAll(')', '')
			.replaceAll('-', '')
			.replaceAll(' ', '');

		updateAssociate({
			variables: {
				input: {
					id,
					...data
				}
			}
		});
	};

	const inputHover = useColorModeValue('appai.main', 'appai.main');
	const textColor = useColorModeValue('gray.700', 'white');

	return (
		<Flex
			direction="column"
			minH="100vh"
			align="center"
			pt={{ sm: '120px', lg: '160px' }}
		>
			<Card w={{ md: '100%', lg: '50%' }}>
				<CardHeader mb="40px">
					<Flex direction="column">
						<Text
							color={textColor}
							fontSize="lg"
							fontWeight="bold"
							mb="3px"
						>
							Editar Associado
						</Text>
					</Flex>
				</CardHeader>
				<CardBody>
					<FormProvider {...methods}>
						<form
							style={{ width: '100%' }}
							onSubmit={handleSubmit(onSubmit)}
						>
							<Flex direction="column" w="100%">
								<Grid
									templateColumns={{
										sm: '1fr',
										md: 'repeat(2, 1fr)'
									}}
									gap="24px"
								>
									<FormControl isInvalid={errors.name}>
										<FormLabel
											color={textColor}
											fontWeight="bold"
											fontSize="xs"
										>
											Nome
										</FormLabel>
										{loading ? (
											<Skeleton height="40px" />
										) : (
											<Input
												{...register('name')}
												defaultValue={
													associate.name || null
												}
												focusBorderColor={inputHover}
												borderRadius="8px"
												fontSize="md"
											/>
										)}
										{errors?.name && (
											<FormErrorMessage>
												{errors?.name?.message}
											</FormErrorMessage>
										)}
									</FormControl>
									<FormControl isInvalid={errors.phone}>
										<FormLabel
											color={textColor}
											fontWeight="bold"
											fontSize="xs"
										>
											Telefone
										</FormLabel>
										{loading ? (
											<Skeleton height="40px" />
										) : (
											<Input
												{...register('phone')}
												as={InputMask}
												mask="(99) 99999-9999"
												defaultValue={
													associate.phone || null
												}
												focusBorderColor={inputHover}
												borderRadius="8px"
												fontSize="md"
											/>
										)}
										{errors?.phone && (
											<FormErrorMessage>
												{errors?.phone?.message}
											</FormErrorMessage>
										)}
									</FormControl>
									<FormControl>
										<HStack alignItems="center" mb="24px">
											<FormLabel
												color={textColor}
												fontWeight="bold"
												fontSize="xs"
												my="auto"
											>
												Ativo
											</FormLabel>
											{loading ? null : (
												<Switch
													{...register('status')}
													defaultChecked={
														associate.status
													}
													colorScheme="purple"
												/>
											)}
										</HStack>
									</FormControl>
								</Grid>
								<Flex
									w="full"
									justifyContent="flex-end"
									mt="24px"
								>
									<HStack spacing={4}>
										<Button
											disabled={loadingUpdate}
											variant="ghost"
											colorScheme="gray"
											alignSelf="flex-end"
											size="md"
											onClick={() => history.goBack()}
										>
											Cancelar
										</Button>
										<Button
											isLoading={loadingUpdate}
											disabled={loadingUpdate}
											colorScheme="purple"
											type="submit"
											alignSelf="flex-end"
											size="md"
										>
											Atualizar
										</Button>
									</HStack>
								</Flex>
							</Flex>
						</form>
					</FormProvider>
				</CardBody>
			</Card>
		</Flex>
	);
};

export default EditAssociate;
